import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shopOwnerService } from "../services/service/shopOwnerService";

const EMPTY_PARAMS = {};
const SHOP_OWNERS_KEY = ["shop-owners"];
const AVAILABLE_OWNERS_KEY = ["shop-owners", "available"];

export const useShopOwners = (shopId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        shopId,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [shopId, currentPage, pageSize, searchTerm, statusFilter, startDate, endDate, initialParams]);

    const query = useQuery({
        queryKey: [...SHOP_OWNERS_KEY, "shop", shopId, params],
        queryFn: () => shopOwnerService.getAll(params),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: SHOP_OWNERS_KEY }),
        [queryClient]
    );

    const createOwner = useCallback(async (data) => {
        const response = await shopOwnerService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateOwner = useCallback(async (id, data) => {
        const response = await shopOwnerService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleOwnerStatus = useCallback(async (id) => {
        const response = await shopOwnerService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const setOwnerStatus = useCallback(async (id, status) => {
    const response = await shopOwnerService.setStatus(id, status);
    await invalidate();
    return response;
}, [invalidate]);

    const getTotalByShop = useCallback(async (shopId) => {
        try {
            const response = await shopOwnerService.getTotalByShop(shopId);
            return response.data?.data || {};
        } catch (error) {
            throw error;
        }
    }, []);

    const getOwnerByUsername = useCallback(async (username) => {
        try {
            const owner = await shopOwnerService.getOwnerByUsername(username);
            return owner;
        } catch (error) {
            throw error;
        }
    }, []);

    const getAvailableOwners = useCallback(async (params = {}) => {
        try {
            const response = await shopOwnerService.getAvailableOwners(params);
            return response.data?.data || [];
        } catch (error) {
            throw error;
        }
    }, []);

    return {
        owners: query.data?.data || [],
        pagination: query.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        refresh: query.refetch,
        createOwner,
        updateOwner,
        toggleOwnerStatus,
        setOwnerStatus,
        getTotalByShop,
        getOwnerByUsername,
        getAvailableOwners,
    };
};

export const useAvailableOwners = (shopId, search = "", enabled = true) => {
    const query = useQuery({
        queryKey: [...AVAILABLE_OWNERS_KEY, shopId, search],
        queryFn: () =>
            shopOwnerService.getAvailableOwners({
                shopId,
                search: search?.trim() || undefined,
            }),
        enabled: Boolean(shopId) && enabled,
        staleTime: 30 * 1000,
    });

    return {
        availableOwners: query.data?.data || [],
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refetch: query.refetch,
    };
};

export const useShopOwner = useShopOwners;
export const useOwners = useShopOwners;